import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../lib/api';
import { getDisplayName, getPictureUrl } from '../lib/liff';
import { applyThemeColors } from '../lib/store';
import EventCard, { type PortalEvent } from '../components/EventCard';
import CategoryTabs from '../components/CategoryTabs';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { IconCard, IconCheck, IconChevronRight, IconSearch, IconUser, IconCalendar, IconX } from '../components/Icons';
import { Screen, SectionTitle, EmptyState, formatNtd, daysLeft, formatDate } from '../components/ui';

interface PortalSettings {
  siteName?: string;
  primaryColor?: string;
  primaryDark?: string;
}

interface RecentBooking {
  id: string;
  eventId?: string;
  eventTitle: string;
  eventDate?: string;
  status: string;
  paymentStatus: string;
  totalAmount?: number;
  createdAt: string;
}

interface HomeData {
  portalSettings?: PortalSettings;
  featured: PortalEvent[];
  upcoming: PortalEvent[];
  closingSoon: PortalEvent[];
  categories: Record<string, number>;
  user: { bookingsCount: number; recentBookings: RecentBooking[] } | null;
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 5) return '晚安';
  if (h < 11) return '早安';
  if (h < 18) return '午安';
  return '晚安';
}

export default function HomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<HomeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const response = await api.getPortalHome();
      if (cancelled) return;
      if (response.success && response.data) {
        setData(response.data);
        applyThemeColors(response.data.portalSettings?.primaryColor, response.data.portalSettings?.primaryDark);
      } else {
        setError(response.error || '無法載入活動');
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const userName = getDisplayName();
  const userPicture = getPictureUrl();

  // One highlighted event, then the rest as rows (closing soon first, no duplicates).
  const { hero, rows } = useMemo(() => {
    const all = [...(data?.featured || []), ...(data?.closingSoon || []), ...(data?.upcoming || [])];
    const seen = new Set<string>();
    const unique = all.filter((e) => (seen.has(e.id) ? false : (seen.add(e.id), true)));
    const open = unique.filter((e) => e.registrationStatus !== 'closed');
    const hero = open[0] || null;
    const rows = unique.filter((e) => e.id !== hero?.id).slice(0, 6);
    return { hero, rows };
  }, [data]);

  const todos = (data?.user?.recentBookings || []).filter((b) => b.status !== 'cancelled').slice(0, 2);
  const pendingCount = (data?.user?.recentBookings || []).filter((b) => b.paymentStatus === 'pending' && b.status !== 'cancelled').length;

  if (loading) return <LoadingSpinner />;

  return (
    <Screen className="pb-28">
      {/* Greeting */}
      <div className="safe-top flex items-center gap-3 px-5 pt-4 pb-2">
        {userPicture ? (
          <img src={userPicture} alt="" className="w-11 h-11 rounded-full object-cover bg-soft" />
        ) : (
          <span className="w-11 h-11 rounded-full bg-primary-soft text-primary inline-flex items-center justify-center"><IconUser size={22} /></span>
        )}
        <div className="flex-1 min-w-0 flex flex-col leading-tight">
          <span className="text-xl font-black truncate">{userName ? `${userName}，${greeting()}` : `${greeting()}`}</span>
          <span className="text-[13px] text-muted">
            {pendingCount > 0 ? `有 ${pendingCount} 筆報名還沒匯款` : data?.portalSettings?.siteName || '喵喵團報'}
          </span>
        </div>
        <Link to="/events?focus=search" aria-label="搜尋活動" className="w-11 h-11 rounded-full bg-soft text-ink inline-flex items-center justify-center active:bg-line">
          <IconSearch size={20} />
        </Link>
      </div>

      <div className="flex flex-col gap-6 px-5 pt-4">
        {error && (
          <div role="alert" className="flex items-center gap-2 px-4 py-3 rounded-[16px] bg-bad-soft text-bad text-[13px]">
            <IconX size={16} />{error}
          </div>
        )}

        {/* To-do: bookings that need attention */}
        {todos.length > 0 && (
          <section className="flex flex-col gap-2.5">
            <SectionTitle>待辦</SectionTitle>
            {todos.map((b) => <TodoCard key={b.id} booking={b} />)}
          </section>
        )}

        {/* Browse */}
        <section className="flex flex-col gap-3">
          <SectionTitle action={<Link to="/events" className="text-[13px] font-medium text-primary">全部活動</Link>}>
            開放報名中
          </SectionTitle>
          <CategoryTabs
            categories={data?.categories || {}}
            selected="all"
            onChange={(c) => navigate(c === 'all' ? '/events' : `/events?category=${c}`)}
          />

          {hero ? (
            <>
              <EventCard event={hero} variant="featured" />
              {rows.map((e) => <EventCard key={e.id} event={e} variant="row" />)}
            </>
          ) : (
            <EmptyState
              icon={<IconCalendar size={28} />}
              title="目前沒有開放報名的活動"
              hint="新活動開放時會在 LINE 通知，記得加入群組。"
            />
          )}
        </section>
      </div>

      <BottomNav active="home" />
    </Screen>
  );
}

function TodoCard({ booking }: { booking: RecentBooking }) {
  const pending = booking.paymentStatus === 'pending';
  const rejected = booking.paymentStatus === 'rejected';
  const dl = daysLeft(booking.eventDate);

  if (pending) {
    return (
      <Link to={`/booking/${booking.id}`} className="flex flex-col gap-3 p-4 rounded-card bg-warn-soft text-ink">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 shrink-0 rounded-[12px] bg-warn-dot text-[#5B3A00] inline-flex items-center justify-center"><IconCard size={18} /></span>
          <div className="flex-1 min-w-0 flex flex-col leading-tight">
            <span className="text-[15px] font-bold truncate">還沒匯款：{booking.eventTitle}</span>
            <span className="text-xs text-warn tabular">
              {booking.totalAmount ? `${formatNtd(booking.totalAmount)} · ` : ''}送出於 {formatDate(booking.createdAt, { month: 'numeric', day: 'numeric' })}
            </span>
          </div>
        </div>
        <span className="h-11 rounded-[12px] bg-ink text-white text-sm font-bold inline-flex items-center justify-center">查看匯款資訊</span>
      </Link>
    );
  }

  return (
    <Link
      to={`/booking/${booking.id}`}
      className={clsx('flex items-center gap-3 px-4 py-3.5 rounded-card text-ink', rejected ? 'bg-bad-soft' : 'bg-ok-soft')}
    >
      <span className={clsx('w-9 h-9 shrink-0 rounded-[12px] text-white inline-flex items-center justify-center', rejected ? 'bg-bad-dot' : 'bg-ok-dot')}>
        {rejected ? <IconX size={18} /> : <IconCheck size={18} />}
      </span>
      <div className="flex-1 min-w-0 flex flex-col leading-tight">
        <span className="text-[15px] font-bold truncate">{booking.eventTitle} {rejected ? '已取消' : '已確認'}</span>
        <span className={clsx('text-xs', rejected ? 'text-bad' : 'text-ok')}>
          {rejected ? '如有疑問請在 LINE 群組詢問' : dl != null && dl >= 0 ? `${formatDate(booking.eventDate)} · 還有 ${dl} 天` : formatDate(booking.eventDate)}
        </span>
      </div>
      <IconChevronRight size={18} className="text-muted shrink-0" />
    </Link>
  );
}
